import { normalizeMobileNumber, mobileNumberError } from './normalizeMobileNumber';
import { useRegister } from '@/common/apis/services/auth/register';
import { useResetPassword } from '@/common/apis/services/auth/resetPassword';
import Button from '@/common/components/atom/button';
import Text from '@/common/components/atom/text/text';
import TextField from '@/common/components/atom/textField';
import { ClinicStatus } from '@/common/constants/status/clinicStatus';
import { getErrorMessage } from '@/common/utils/errorHandler';
import { normalizeDigitInput } from '@/common/utils/normalizeDigitInput';
import { sanitizeRedirectUrl } from '@/common/utils/sanitizeRedirectUrl';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import config from 'next/config';
import { Dispatch, FormEvent, SetStateAction, useState } from 'react';
import { toast } from 'react-hot-toast';
import { StepLoginForm } from '../../views/loginForm';
import LoginTitleBar from '../titleBar';
import Divider from '@/common/components/atom/divider';
import useCustomize from '@/common/hooks/useCustomize';
const { publicRuntimeConfig } = config();
import ITOLogo from '../../assets/ITOLogo.png';
import GoogleLogo from '../../assets/google.svg';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { splunkInstance } from '@/common/services/splunk';
import { useRouter } from 'next/router';
interface MobileNumberProps {
  title?: string;
  description?: string;
  setStep: Dispatch<SetStateAction<StepLoginForm>>;
  mobileNumberValue: string;
  setMobileNumberValue: Dispatch<SetStateAction<string>>;
}

export const MobileNumber = (props: MobileNumberProps) => {
  const { mobileNumberValue, setMobileNumberValue, setStep, title, description } = props;
  const { t } = useTranslation('login');
  const register = useRegister();
  const resetPassword = useResetPassword();
  const [fieldError, setFieldError] = useState('');
  const [googleOauthLoading, setGoogleOauthLoading] = useState(false);
  const [oidcOauthLoading, setOidcOauthLoading] = useState(false);
  const showGoogleLogin = useFeatureIsOn('aaa:google-login-button');
  const showOidcLogin = useFeatureIsOn('aaa:oidc-login-button');
  const university = useCustomize(state => state.customize?.partnerKey);
  const router = useRouter();


  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeMobileNumber(mobileNumberValue);
    const error = mobileNumberError(mobileNumberValue);
    setFieldError(error);
    if (error) return;
    setMobileNumberValue(normalized);

    try {
      splunkInstance('gozargah').sendEvent({
        group: 'legacy-login-steps',
        type: 'submit-mobile-number',
      });
      window?.clarity?.('event', 'login_mobile_submitted');

      const { data: registerRes } = await register.mutateAsync({
        cell: +normalized,
      });

      if (registerRes.status === 0) {
        const errorMessage = registerRes.details?.['تلفن همراه'] || 'خطا در ثبت شماره موبایل';
        return toast.error(errorMessage);
      }

      const { data: resetPasswordRes } = await resetPassword.mutateAsync({
        cell: +normalized,
      });

      if (resetPasswordRes.status === ClinicStatus.SUCCESS || resetPasswordRes.status === 39) {
        if (resetPasswordRes.status === 39) {
          const errorMessage = resetPasswordRes.message || 'خطا در ارسال کد تایید';
          toast.error(errorMessage);
        }
        if (resetPasswordRes?.result?.has_static_password) return setStep('password');
        return setStep('otp_code');
      }

      const errorMessage = resetPasswordRes.message || 'خطا در ارسال کد تایید';
      toast.error(errorMessage);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  return (
    <form className="flex flex-col space-y-5" onSubmit={handleRegister}>
      <LoginTitleBar title={title ?? t('steps.mobileNumber.title')} description={description ?? t('steps.mobileNumber.description')} />
      <TextField
        label={t('steps.mobileNumber.phoneNumberFieldLable')}
        onChange={e => {
          const raw = e.target.value.replace(/[٠-٩]/g, char => String(char.charCodeAt(0) - 0x660));
          const digits = normalizeDigitInput(raw);
          const international = digits.startsWith('0098') || digits.startsWith('98');
          setMobileNumberValue(international ? '0' + digits.slice(digits.startsWith('0098') ? 4 : 2).replace(/^0/, '') : digits.startsWith('9') && digits.length > 1 ? `0${digits}` : digits);
          setFieldError('');
        }}
        value={mobileNumberValue}
        style={{ direction: 'ltr' }}
        placeholder="09"
        helperText={fieldError}
        error={!!fieldError}
        autoFocus
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        name="tel"
        disabled={register.isLoading || resetPassword.isLoading}
      />

      <Button type="submit" loading={register.isLoading || resetPassword.isLoading}>
        {t('steps.mobileNumber.action')}
      </Button>
      {(showOidcLogin || showGoogleLogin) && (
        <div className="flex flex-col !mt-3 gap-2">
          <div className="flex justify-center gap-3 items-center mb-1">
            <Divider />
            <span className="text-sm font-semibold">یا</span>
            <Divider />
          </div>
          {showOidcLogin && (
            <Button
              type="button"
              loading={oidcOauthLoading}
              icon={<img src={ITOLogo.src} className="h-6 w-7" alt="ITO Logo" />}
              variant="secondary"
              onClick={() => {
                splunkInstance('gozargah').sendEvent({
                  group: 'legacy-login-steps',
                  type: 'login-with-oidc',
                });
                setOidcOauthLoading(true);

                const origin = window.location.origin;
                const redirectUrl =
                  sanitizeRedirectUrl(router.query?.redirect_url) ??
                  `${window.location.pathname}${window.location.search}`;

                const authServiceUrl = `https://user.paziresh24.com/auth/gov?origin=${encodeURIComponent(
                  origin,
                )}&redirect_url=${encodeURIComponent(redirectUrl)}`;

                location.assign(authServiceUrl);
              }}
            >
              ورود از طریق دولت من
            </Button>
          )}
          {showGoogleLogin && (
            <Button
              type="button"
              loading={googleOauthLoading}
              icon={<img src={GoogleLogo.src} className="h-6 w-7" alt="Google Logo" />}
              variant="secondary"
              onClick={() => {
                splunkInstance('gozargah').sendEvent({
                  group: 'legacy-login-steps',
                  type: 'login-with-google',
                });
                window?.clarity?.('upgrade', 'google login');
                setGoogleOauthLoading(true);

                // ۱. مشخص کردن آدرسی که کاربر الان توش هست و آدرسی که باید بعدا برگرده
                const origin = window.location.origin; // مثلاً https://www.paziresh24.com
                const redirectUrl =
                  sanitizeRedirectUrl(router.query?.redirect_url) ??
                  `${window.location.pathname}${window.location.search}`;

                // ۲. ریدایرکت مستقیم به وب‌سرویس خودت (بک‌اِند کرسر امنیت و ریدایرکت به گوگل را هندل می‌کند)
                const authServiceUrl = `https://user.paziresh24.com/auth/google?origin=${encodeURIComponent(
                  origin,
                )}&redirect_url=${encodeURIComponent(redirectUrl)}${university ? '&university=true' : ''}`;

                location.assign(authServiceUrl);
              }}
            >
              ورود از طریق گوگل
            </Button>
          )}
        </div>
      )}

      <Text align="center" fontWeight="medium" className="leading-5 text-[0.7rem] md:text-xs">
        <Trans
          i18nKey="login:steps.mobileNumber.termAndprivacyPolicyNotice"
          components={[
            <a key={0} href={`${publicRuntimeConfig.CLINIC_BASE_URL}/home/rules/`} target="_blank" className="text-blue-700" />,
            <a key={1} href="/privacy-policy/" target="_blank" className="text-blue-700" />,
          ]}
        />
      </Text>
    </form>
  );
};

export default MobileNumber;

